import MoveHouseInfosForm from './move-house-infos.form';
import React, { ChangeEvent } from 'react';
import { ReferenceDto } from '../../../services/dtos/reference.dto';
import { Form, Formik } from 'formik';
import { mixed, object, string } from 'yup';
import MoveHouseOtherInfosForm from './move-house-other-infos.form';
import { Button, Grid, Typography } from '@mui/material';
import './move-house.form.scss';
import { ERROR_MAIL } from '../../../utils/constants.utils';
import { Link } from 'react-router-dom';
import { AddressGouvFeaturesPropertiesFormDto } from '../../../services/dtos/form/address-gouv-features-properties-form.dto';

/**
 * Formualire global avec toutes les règles et valeurs par defauts
 * @param props
 * @constructor
 */
export default function MoveHouseForm(props: {
    //handle lors du changement de pays
    handleChangeCountry: (e: React.SyntheticEvent, v: ReferenceDto | null) => void;
    //handle lors du changement de pays entrant
    handleChangeCountryIncomingPerson: (e: React.SyntheticEvent, v: ReferenceDto | null) => void;
    //handle lors du submit
    handleSumbitForm: (values: any) => void;
    //List des pays
    countriesSelection: ReferenceDto[];
    //handle lors du changement de valeur connait entrant
    handleChangeIncomingPerson: (e: ChangeEvent<HTMLInputElement>, value: string) => void;
    //valeur connait entrant
    incomingPerson: string;
    //valeur si personne moral
    isMoral: boolean;
    //handle lors du changement de valeur d'interlocuteur
    handleChangeInterlocutor: (interlocutor: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
    //liste des interlocuteurs
    interlocutorsSelection: { value: string; label: string }[];
    //liste des civilités
    civilities: ReferenceDto[];
    //validate
    validate: boolean;
    //Identifiant du contrat
    contractId?: string;
    //Pays sélectionné pour l'adresse principale
    selectedCountry?: ReferenceDto;
    //Pays sélectionné pour la personne entrante
    selectedIncomingPersonCountry?: ReferenceDto;
    //Handle pour la recherche d'adresse principale
    handleSearchAddress?: (event: object, value: string, reason: string) => void;
    //Handle pour la recherche d'adresse de la personne entrante
    handleSearchAddressIncoming?: (event: object, value: string, reason: string) => void;
    //Liste des adresses gouvernementales pour l'adresse principale
    addressesGouv?: AddressGouvFeaturesPropertiesFormDto[];
    //Liste des adresses gouvernementales pour la personne entrante
    addressesGouvIncoming?: AddressGouvFeaturesPropertiesFormDto[];
    //Handle pour la sélection d'une adresse principale
    handleSelectAddress?: (address: AddressGouvFeaturesPropertiesFormDto | null) => void;
    //Handle pour la sélection d'une adresse de la personne entrante
    handleSelectAddressIncoming?: (address: AddressGouvFeaturesPropertiesFormDto | null) => void;
}) {
    //Constantes
    const messageCaracteresMin = "Merci d'entrer deux caractères minimum";
    const nbCaracteresMin = 2;
    const nbCaracteresTel = 9;
    const nbCaracteresMaxMeterReading = 5;

    // Schéma de validation des données
    const validationSchema = object({
        //Validation de la date de sortie
        dateMoving: string().trim().required('Veuillez renseigner la date de sortie'),
        //Validation relevé de compteur
        meterReading: string()
            .trim()
            .required('Veuillez renseigner votre relevé de compteur actuel')
            .max(nbCaracteresMaxMeterReading, "Merci d'entrer cinq caractères maximum"),
        //Validation relevé de compteur
        numeroRue: string().trim().required('Veuillez renseigner votre numéro de rue'),
        //Validation de l'adresse
        rue: string().trim().required('Veuillez renseigner votre nom de rue'),
        ville: string().trim().required('Veuillez renseigner votre ville de résidence'),
        pays: mixed<ReferenceDto>().required('Veuillez choisir un pays'),
        codePostal: string().trim().required('Veuillez renseigner votre code postal').matches(/^\d+$/, {
            message: 'Seul les caractères numériques sont autorisés',
        }),
        //Validation des champs de l'entrant
        entrant: object().shape({
            //Validartion du type d'entrant
            //Conditionné incomingPerson
            type: mixed<ReferenceDto>().when(() =>
                props.incomingPerson === 'Non'
                    ? mixed<ReferenceDto>().notRequired()
                    : mixed<ReferenceDto>().required("Le type d'interlocuteur est obligatoire")
            ),
            //Validation de la civilité
            //Conditionné incomingPerson
            civilite: mixed<ReferenceDto>().when(() =>
                props.incomingPerson === 'Non' || props.isMoral
                    ? mixed<ReferenceDto>().notRequired()
                    : mixed<ReferenceDto>().required('La civilité est obligatoire')
            ),
            //Validation du nom
            //Conditionné incomingPerson
            nomUsage: string().when(() =>
                props.incomingPerson === 'Non' || props.isMoral
                    ? string().trim().notRequired()
                    : string()
                          .trim()
                          .required('Le nom est obligatoire')
                          .matches(
                              /^[A-Za-z-áàâäãåçéèêëíìîïñóòôöõúùûüýÿæœÁÀÂÄÃÅÇÉÈÊËÍÌÎÏÑÓÒÔÖÕÚÙÛÜÝŸÆŒ ]+$/,
                              "Seul les caractères alphabétiques, le tiret (-) et l'apostrophe (') sont autorisés"
                          )
                          .min(nbCaracteresMin, messageCaracteresMin)
            ),
            //Validation du prénom
            //Conditionné incomingPerson
            prenom: string().when(() =>
                props.incomingPerson === 'Non' || props.isMoral
                    ? string().notRequired()
                    : string()
                          .trim()
                          .matches(
                              /^[A-Za-z-áàâäãåçéèêëíìîïñóòôöõúùûüýÿæœÁÀÂÄÃÅÇÉÈÊËÍÌÎÏÑÓÒÔÖÕÚÙÛÜÝŸÆŒ ]+$/,
                              "Seul les caractères alphabétiques, le tiret - et l'apostrophe ' sont autorisés"
                          )
                          .min(nbCaracteresMin, messageCaracteresMin)
                          .required('Le prénom est obligatoire')
            ),
            //Controle du champ tel portable
            //Controle :  taille / format
            telPort: string()
                .matches(/^\d+$/, {
                    message: 'Caractère non valide inséré.',
                })
                .length(nbCaracteresTel, "Merci d'entrer 9 caractères."),
            //Controle du champ tel fixe
            //Controle :  taille / format
            telFixe: string()
                .matches(/^\d+$/, {
                    message: 'Caractère non valide inséré.',
                })
                .length(nbCaracteresTel, "Merci d'entrer 9 caractères."),
            //Controle du champ mail
            //Controle : taille / format
            mail: string()
                .email(ERROR_MAIL)
                .matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, ERROR_MAIL),
            //Validation de l'adresse de l'entrant
            adresse: object().shape({
                //Validation du champ rue
                //Conditionné incomingPerson
                rue: string()
                    .trim()
                    .when(() =>
                        props.incomingPerson === 'Non'
                            ? mixed<ReferenceDto>().notRequired()
                            : mixed<ReferenceDto>().required('Veuillez renseigner votre nom de rue')
                    ),
                //Validation du champ ville
                //Conditionné incomingPerson
                ville: string()
                    .trim()
                    .when(() =>
                        props.incomingPerson === 'Non'
                            ? mixed<ReferenceDto>().notRequired()
                            : mixed<ReferenceDto>().required('Veuillez renseigner votre ville de résidence')
                    ),
                //Validation du champ pays
                //Conditionné incomingPerson
                pays: mixed<ReferenceDto>().when(() =>
                    props.incomingPerson === 'Non'
                        ? mixed<ReferenceDto>().notRequired()
                        : mixed<ReferenceDto>().required('Veuillez choisir un pays')
                ),
                //Validation du champ CP
                //Conditionné incomingPerson | match
                codePostal: string()
                    .trim()
                    .when(() =>
                        props.incomingPerson === 'Non'
                            ? mixed<ReferenceDto>().notRequired()
                            : mixed<ReferenceDto>().required('Veuillez renseigner votre code postal')
                    )
                    .matches(/^\d+$/, {
                        message: 'Seul les caractères numériques sont autorisés',
                    }),
            }),
        }),
    });

    // Valeurs par défaut
    const iniValues = {
        dateMoving: '',
        meterReading: '',
        numeroRue: '',
        rue: '',
        complement: {
            numeroRue: '',
            rue: '',
            batiment: '',
            etage: '',
            appartement: '',
            escalier: '',
            ville: '',
        },
        ville: '',
        codePostal: '',
        boitePostale: '',
        pays: { value: '', label: '' },
        entrant: {
            type: '',
            civilite: '',
            nomUsage: '',
            prenom: '',
            dateNaissance: '',
            lieuNaissance: '',
            telPort: '',
            telFixe: '',
            mail: '',
            adresse: {
                rue: '',
                complement: {
                    numeroRue: '',
                    rue: '',
                    batiment: '',
                    etage: '',
                    appartement: '',
                    escalier: '',
                    ville: '',
                },
                codePostal: '',
                ville: '',
                boitePostale: '',
                pays: { value: '', label: '' },
            },
        },
    };

    //Retourne les deux composants
    //Premier composant possède les informations de la personne courante
    //Second composant possède les informations de la personne entrant si connu
    return (
        <Formik initialValues={iniValues} onSubmit={props.handleSumbitForm} validationSchema={validationSchema}>
            <Form>
                <Typography variant="h4">Demande de déménagement</Typography>
                <Grid container spacing={2} className="move-house-infos">
                    {/*vue informations de la personne courrante*/}
                    <MoveHouseInfosForm
                        countriesSelection={props.countriesSelection}
                        handleChangeCountry={props.handleChangeCountry}
                        selectedCountry={props.selectedCountry}
                        handleSearchAddress={props.handleSearchAddress}
                        addressesGouv={props.addressesGouv}
                        handleSelectAddress={props.handleSelectAddress}
                    />
                    {/*Vue informations entrant*/}
                    <MoveHouseOtherInfosForm
                        handleChangeIncomingPerson={props.handleChangeIncomingPerson}
                        incomingPerson={props.incomingPerson}
                        countriesSelection={props.countriesSelection}
                        handleChangeCountry={props.handleChangeCountry}
                        handleChangeCountryIncomingPerson={props.handleChangeCountryIncomingPerson}
                        isMoral={props.isMoral}
                        handleChangeInterlocutor={props.handleChangeInterlocutor}
                        interlocutorsSelection={props.interlocutorsSelection}
                        civilities={props.civilities}
                        selectedIncomingPersonCountry={props.selectedIncomingPersonCountry}
                        handleSearchAddressIncoming={props.handleSearchAddressIncoming}
                        addressesGouvIncoming={props.addressesGouvIncoming}
                        handleSelectAddressIncoming={props.handleSelectAddressIncoming}
                    />
                    {/*Emplacement des boutons annuler et envoyer*/}
                    <Grid item xs={12} className={'buttonsForm'}>
                        <Typography variant={'button'}>
                            {/*Redirection du la page contrat*/}
                            <Button variant="neutral" component={Link} to={'/contrat/' + props.contractId}>
                                Annuler
                            </Button>
                        </Typography>
                        {/*Envoi du formulaire*/}
                        <Typography variant={'button'}>
                            <Button type="submit" variant="contained" disabled={!props.validate}>
                                Envoyer
                            </Button>
                        </Typography>
                    </Grid>
                </Grid>
            </Form>
        </Formik>
    );
}
